import type { ZudokuConfig } from "zudoku";
import { Badge } from "zudoku/ui/Badge";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardFooter,
  CardDescription,
} from "zudoku/ui/Card";

import { Alert, AlertDescription, AlertTitle } from "zudoku/ui/Alert";
import { Head } from "zudoku/components";
import PreoWidget from "./components/PreoWidget";
import PreoConfigEditor from "./components/PreoConfigEditor";
import ApiCredentialsForm from "./components/ApiCredentialsForm";
import { backofficeIdentityPlugin } from "./lib/backofficeIdentityPlugin";
import { matomoPlugin } from "./lib/matomoPlugin";

// Auth toggle for local dev convenience (defaults to enabled)
const isAuthEnabled = process.env.ZUDOKU_PUBLIC_AUTH_ENABLED !== "false";

const navigation = [
  {
    type: "doc",
    label: "Welcome",
    icon: "home",
    file: "index",
    path: "/",
    display: "hide",
  },
  {
    type: "category",
    label: "Documentation",
    display: "hide",
    items: [
      {
        type: "category",
        label: "Concepts",
        collapsed: false,
        items: [
          "documentation/concepts/index",
          "documentation/concepts/product-types",
          "documentation/concepts/purchase-limits",
        ],
      },
      {
        type: "category",
        label: "Best Practices",
        collapsed: false,
        items: ["documentation/best-practices/index"],
      },
      {
        type: "category",
        label: "Operations",
        collapsed: false,
        items: ["documentation/operations/index"],
      },
      {
        type: "category",
        label: "Apps",
        collapsed: false,
        items: ["documentation/apps/index"],
      },
      {
        type: "category",
        label: "Backoffice",
        collapsed: false,
        items: [
          "documentation/backoffice/index",
          "documentation/backoffice/data-import",
        ],
      },
      {
        type: "category",
        label: "Troubleshooting",
        collapsed: false,
        items: ["documentation/troubleshooting/index"],
      },
    ],
  },
  {
    type: "category",
    label: "Integration",
    icon: "layers",
    collapsed: false,
    items: [
      {
        type: "doc",
        label: "Introduction",
        file: "integration/intro",
      },
      {
        type: "category",
        label: "Web Component",
        collapsed: false,
        items: [
          "integration/preview",
          "integration/options",
          "integration/status",
        ],
      },
      {
        type: "category",
        label: "Frameworks",
        collapsed: false,
        items: ["integration/nuxt", "integration/nextjs", "integration/wordpress"],
      },
    ],
  },
  {
    type: "category",
    label: "API",
    icon: "code",
    items: [
      { type: "doc", file: "api/intro", label: "Introduction", path: "/api" },
      "api/credentials",
      { type: "link", to: "/api/management", label: "Management API" },
      { type: "link", to: "/api/wallet", label: "Wallet API" },
      { type: "link", to: "/api/webhooks", label: "Webhooks" },
      // "api/claude-skill",
    ],
  },
];

const apiDefinitions = [
  {
    path: "/api/management",
    type: "file",
    input: "./public/specs/main.yml",
    options: { disablePlayground: false, expandApiInformation: true },
  },
  {
    path: "/api/wallet",
    type: "file",
    input: "./public/specs/edge-wallet.yml",
    options: { disablePlayground: false },
  },
  {
    path: "/api/webhooks",
    type: "file",
    input: "./public/specs/webhooks.yml",
    options: { disablePlayground: false },
  },
];

const config: ZudokuConfig = {
  site: {
    logo: {
      width: 120,
      src: {
        dark: "https://assets.preo.cloud/logo/preo-logo-dark-bg.png",
        light: "https://assets.preo.cloud/logo/preo-logo-light-bg.png",
      },
    },
    showPoweredBy: false,
    footer: {
      columns: [
        {
          title: "Preo",
          links: [
            { label: "Homepage", href: "https://preo.live" },
            { label: "About", href: "https://preo.live/about" },
          ],
        },
        {
          title: "Apps",
          links: [
            { label: "Dashboard", href: "https://backoffice.preo.cloud" },
            {
              label: "App Store",
              href: "https://apps.apple.com/dk/developer/preo-aps/id1683828233",
            },
            {
              label: "Google Play",
              href: "https://play.google.com/store/apps/developer?id=Preo.live&hl=en",
            },
          ],
        },
        {
          title: "Social",
          links: [
            {
              label: "LinkedIn",
              href: "https://www.linkedin.com/company/preo-live",
            },
          ],
        },
      ],
      copyright: `© ${new Date().getFullYear()} Preo ApS`,
    },
  },
  navigation,
  apis: apiDefinitions,
  docs: {
    files: "/pages/**/*.{md,mdx}",
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
    },
  },
  // Authentication only when enabled
  // ...(isAuthEnabled
  //   ? {
  //       authentication: {
  //         type: "clerk",
  //         clerkPubKey:
  //           "pk_test_cHJvcGVyLXJlZGJpcmQtNTEuY2xlcmsuYWNjb3VudHMuZGV2JA",
  //       },
  //       protectedRoutes: ["*"],
  //     }
  //   : {}),
  plugins: [backofficeIdentityPlugin, matomoPlugin],
  mdx: {
    components: {
      // Custom components
      PreoWidget,
      PreoConfigEditor,
      ApiCredentialsForm,
      // Zudoku components
      Head,
      Alert,
      AlertDescription,
      AlertTitle,
      Badge,
      Card,
      CardHeader,
      CardContent,
      CardTitle,
      CardFooter,
      CardDescription,
    },
  },
  search: {
    type: "pagefind",
    maxSubResults: 3,
  },
  theme: {
    light: {
      background: "oklch(100% 0 none)",
      foreground: "oklch(13.7% 0.036 259)",
      card: "oklch(100% 0 none)",
      cardForeground: "oklch(13.7% 0.036 259)",
      popover: "oklch(100% 0 none)",
      popoverForeground: "oklch(13.7% 0.036 259)",
      primary: "oklch(20.8% 0.04 266)",
      primaryForeground: "oklch(98.4% 0.003 248)",
      secondary: "oklch(96.8% 0.007 248)",
      secondaryForeground: "oklch(20.8% 0.04 266)",
      muted: "oklch(96.8% 0.007 248)",
      mutedForeground: "oklch(55.5% 0.041 257)",
      accent: "oklch(96.8% 0.007 248)",
      accentForeground: "oklch(20.8% 0.04 266)",
      destructive: "oklch(63.7% 0.208 25.3)",
      destructiveForeground: "oklch(98.4% 0.003 248)",
      border: "oklch(92.9% 0.013 256)",
      input: "oklch(92.9% 0.013 256)",
      ring: "oklch(13.7% 0.036 259)",
      radius: "1rem",
    },
    dark: {
      background: "oklch(13.7% 0.036 259)",
      foreground: "oklch(98.4% 0.003 248)",
      card: "oklch(13.7% 0.036 259)",
      cardForeground: "oklch(98.4% 0.003 248)",
      popover: "oklch(13.7% 0.036 259)",
      popoverForeground: "oklch(98.4% 0.003 248)",
      primary: "oklch(98.4% 0.003 248)",
      primaryForeground: "oklch(20.8% 0.04 266)",
      secondary: "oklch(28% 0.037 260)",
      secondaryForeground: "oklch(98.4% 0.003 248)",
      muted: "oklch(28% 0.037 260)",
      mutedForeground: "oklch(71.1% 0.035 257)",
      accent: "oklch(28% 0.037 260)",
      accentForeground: "oklch(98.4% 0.003 248)",
      destructive: "oklch(39.6% 0.133 25.7)",
      destructiveForeground: "oklch(98.4% 0.003 248)",
      border: "oklch(28% 0.037 260)",
      input: "oklch(28% 0.037 260)",
      ring: "212.7 26.8% 83.9",
      radius: "1rem",
    },
  },
};

export default config;
